import React, { useState, useEffect } from "react";
import { showToast } from "./toast.js";
import { wsRequest, getDemoRouteToken } from "./wsClient.js";
import { announceAuthChange } from "./authPrompt.js";
import OtpCodeInput from "./OtpCodeInput.jsx";

const USER_TOKEN_KEY = "589comps_user_token";

// onSuccess is for caller-specific reactions (closing a dropdown, closing
// a modal and retrying whatever action prompted the login) — the actual
// shared auth state update happens via announceAuthChange, which every
// component tracking the logged-in user subscribes to, so this form
// doesn't need to know who else is listening.
//
// preserveExistingSession: tells the backend not to supersede any other
// currently-active session on this same account when this login/signup
// succeeds. It was written for the partner popup login (EmbedLoginPage),
// retired 20 Sep 2026; no current caller passes it. Defaults to false —
// normal, single-session-per-account behaviour.
//
// persistSession: whether a successful login leaves a session behind in
// this origin's localStorage. Defaults to true, which is what every
// normal use of this form on the main site wants. The retired partner
// popup (EmbedLoginPage, removed 20 Sep 2026) passed false, and the
// reason is kept because it applies to any future caller: the original reasoning
// there got it wrong: that popup runs on OUR origin, so persisting here
// silently signs the person in to the provider's own site as a side
// effect of logging in to a partner's. The old comment justified it as
// harmless because the popup's localStorage is isolated from the
// third-party page's by origin rules — true, and beside the point. The
// isolation that mattered was never from the partner; it's that
// localStorage is SHARED with every other tab on our own domain, and
// closing a popup doesn't clear it.
//
// Two things that made that worth fixing rather than tolerating: the
// person can't log out of it from the partner site (different origin,
// so a partner-side logout cannot reach it), and on a shared computer
// they'd leave a live session on a site they never knowingly visited.
// embedMode WAS HERE, for the partner popup login, which swapped in
// auth.embedLogin / auth.embedTotpVerify. Both the popup and those
// actions were retired on 20 Sep 2026.
export default function AuthForm({ onSuccess, preserveExistingSession = false, persistSession = true }) {
  // Signup is deliberately unavailable on the demo-preview route — the
  // confirmed design is logging in via the admin-created demo accounts
  // specifically, not creating a new, real account through it. A real
  // signup would still only ever interact with demo data while on this
  // page (the route's own token overrides whatever mode the account
  // itself carries), but the account would persist afterward as a real,
  // Live-tagged user cluttering the actual site — exactly the kind of
  // mixing this whole mode system exists to prevent.
  const isDemoRoute = !!getDemoRouteToken();
  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [legalAgreed, setLegalAgreed] = useState(false);
  const [emailUpdatesOptIn, setEmailUpdatesOptIn] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [legalDocs, setLegalDocs] = useState([]);

  // EVERY FAILURE IS A TOAST. No exceptions, deliberately.
  //
  // This briefly showed credential errors inline and policy refusals as
  // toasts, on the theory that "incorrect email or password" belongs
  // beside the fields. It does not: that message is deliberately NOT
  // field-specific — it never says which of the two was wrong, so it
  // cannot be used to discover which addresses have accounts. It is not
  // an error attached to a field, it is just an error.
  //
  // And two mechanisms means a decision every time a message is added,
  // which is exactly how six admin panels drifted into inline messages
  // before being converted back on 29 and 30 Aug 2026. One place to look
  // is worth more than the distinction was.
  const reportAuthError = (err) => {
    showToast(err?.message || "Could not reach backend.", "error");
  };
  const [busy, setBusy] = useState(false);
  // Set only once auth.login signals a TOTP-enabled account needs its
  // second factor before a real session is issued — pendingToken is
  // the short-lived credential that carries across to
  // auth.totp.verifyLogin, never used for anything else.
  const [pendingTotp, setPendingTotp] = useState(null); // { pendingToken }
  const [totpCode, setTotpCode] = useState("");
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [backupCodeInput, setBackupCodeInput] = useState("");
  const [totpAttempt, setTotpAttempt] = useState(0); // bumped on each failed attempt, to remount OtpCodeInput fresh (re-focuses box 1)

  // Same source the footer already uses — only a document root has
  // actually published (HTML content or an uploaded PDF) gets a real
  // link; an unpublished one falls back to plain, unlinked text instead
  // of a dead link, but the sentence itself always names all three.
  useEffect(() => {
    if (mode !== "signup") return;
    wsRequest("legalDocs.list")
      .then(setLegalDocs)
      .catch(() => {});
  }, [mode]);

  const inputStyle = {
    padding: 8,
    marginRight: 8,
    marginBottom: 6,
    background: "var(--bg-input)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 6,
    color: "var(--text-primary)",
    display: "block",
    width: "100%",
    boxSizing: "border-box",
  };

  const legalLink = (key, label) => {
    const doc = legalDocs.find((d) => d.key === key);
    const url = doc?.htmlUrl || doc?.pdfUrl;
    return url ? (
      <a key={key} href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    ) : (
      <span key={key}>{label}</span>
    );
  };

  const completeLogin = (data) => {
    // announceAuthChange still fires either way — it's in-page state for
    // whatever is currently mounted, not persistence, and the popup's
    // own render depends on it.
    if (persistSession) localStorage.setItem(USER_TOKEN_KEY, data.token);
    announceAuthChange(data.user, data.token);
    onSuccess?.(data.user, data.token);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body =
        mode === "login"
          ? { email, password, preserveExistingSession }
          : { email, password, username, legalAgreed, emailUpdatesOptIn, preserveExistingSession };
      const action = mode === "login" ? "auth.login" : "auth.signup";
      const data = await wsRequest(action, body);
      if (data.needsTotp) {
        setPendingTotp({ pendingToken: data.pendingToken });
      } else {
        completeLogin(data);
      }
    } catch (err) {
      reportAuthError(err);
    }
    setBusy(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest("auth.forgotPassword", { email });
      // Always shows the same confirmation regardless of whether the
      // email actually belongs to an account — same non-revealing
      // behavior as the backend itself, for the same reason (an
      // account-enumeration signal shouldn't exist on either side).
      setForgotSent(true);
    } catch (err) {
      reportAuthError(err);
    }
    setBusy(false);
  };

  const submitTotpCode = async (e, explicitCode) => {
    e?.preventDefault?.();
    const codeToSubmit = explicitCode ?? (usingBackupCode ? backupCodeInput : totpCode);
    setBusy(true);
    try {
      const data = await wsRequest("auth.totp.verifyLogin", {
        pendingToken: pendingTotp.pendingToken,
        code: codeToSubmit,
        preserveExistingSession,
      });
      completeLogin(data);
    } catch (err) {
      reportAuthError(err);
      setTotpCode("");
      setTotpAttempt((a) => a + 1);
    }
    setBusy(false);
  };

  if (pendingTotp) {
    return (
      <form onSubmit={submitTotpCode}>
        {usingBackupCode ? (
          <>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>Enter one of your backup codes.</div>
            <input
              type="text"
              inputMode="text"
              maxLength={8}
              placeholder="Backup code"
              required
              autoFocus
              value={backupCodeInput}
              onChange={(e) => setBackupCodeInput(e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 8))}
              style={inputStyle}
            />
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>
              Enter the 6-digit code from your authenticator app.
            </div>
            <div style={{ marginBottom: 10 }}>
              <OtpCodeInput
                key={totpAttempt}
                length={6}
                value={totpCode}
                onChange={setTotpCode}
                onComplete={(fullCode) => submitTotpCode(null, fullCode)}
                autoFocus
                disabled={busy}
              />
            </div>
          </>
        )}
        <button type="submit" disabled={busy || (usingBackupCode ? backupCodeInput.length < 6 : totpCode.length !== 6)}>
          {busy ? "…" : "Verify"}
        </button>{" "}
        <button
          type="button"
          onClick={() => {
            setPendingTotp(null);
            setTotpCode("");
            setBackupCodeInput("");
            setUsingBackupCode(false);
          }}
        >
          Back
        </button>
        <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
          {usingBackupCode ? (
            <button
              type="button"
              onClick={() => {
                setUsingBackupCode(false);
                setBackupCodeInput("");
              }}
              style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}
            >
              Use a code from your app instead
            </button>
          ) : (
            <>
              Lost access to your authenticator app?{" "}
              <button
                type="button"
                onClick={() => {
                  setUsingBackupCode(true);
                  setTotpCode("");
                }}
                style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}
              >
                Use one of your backup codes
              </button>
              .
            </>
          )}
        </div>
      </form>
    );
  }

  if (mode === "forgot") {
    return (
      <form onSubmit={submitForgot}>
        {forgotSent ? (
          <>
            <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>
              If that email belongs to an account, a reset link has been sent to it.
            </div>
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setForgotSent(false);
              }}
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>Enter your account's email address.</div>
            <input
              type="email"
              placeholder="Email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
            <button type="submit" disabled={busy}>
              {busy ? "…" : "Send reset link"}
            </button>{" "}
            <button type="button" onClick={() => setMode("login")}>
              Back to login
            </button>
          </>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={submit}>
      <input
        type="email"
        placeholder="Email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={inputStyle}
      />
      <input
        type="password"
        placeholder="Password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        style={inputStyle}
      />

      {mode === "signup" && (
        <>
          <input
            placeholder="Username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={inputStyle}
          />
          <div style={{ fontSize: 11, opacity: 0.55, marginBottom: 6 }}>
            Your name, date of birth, address, and payout details can all be added later from your account page.
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, marginBottom: 8 }}>
            <input
              type="checkbox"
              required
              checked={legalAgreed}
              onChange={(e) => setLegalAgreed(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              I have read, understood and agree to the {legalLink("terms", "terms & conditions")},{" "}
              {legalLink("privacy", "privacy policy")} and {legalLink("disclaimers", "disclaimers")}
            </span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={emailUpdatesOptIn}
              onChange={(e) => setEmailUpdatesOptIn(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>Send me occasional site updates by email (optional — you can change this anytime in your account settings)</span>
          </label>
        </>
      )}

      <button type="submit" disabled={busy}>
        {busy ? "…" : mode === "login" ? "Log in" : "Sign up"}
      </button>{" "}
      {!isDemoRoute && (
        <button type="button" onClick={() => setMode(mode === "login" ? "signup" : "login")}>
          {mode === "login" ? "Need an account?" : "Have an account?"}
        </button>
      )}
      {isDemoRoute && (
        <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
          Sign-up isn't available on this preview — log in with one of the demo accounts provided.
        </div>
      )}
      {mode === "login" && !isDemoRoute && (
        <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
          <button
            type="button"
            onClick={() => setMode("forgot")}
            style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}
          >
            Forgot your password?
          </button>
        </div>
      )}
    </form>
  );
}
